import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation } from "react-router";
import { LoadingState } from "@/components/states";
import { ApiError, currentUserQuery } from "@/lib/api";

/**
 * Sends first-login callers into the onboarding flow and keeps everyone else out. A newcomer
 * with nothing saved lands on step 1; a reload resumes at the saved step because the page
 * reads the same state. Once the flow is complete it never shows again: completed callers
 * land on the app, and anyone opening /welcome directly leaves it. A caller with no session is
 * sent to the sign-in page and returns to where they were afterwards.
 */
export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const me = useQuery(currentUserQuery);
  const location = useLocation();
  const onWelcome = location.pathname === "/welcome";

  if (me.isPending) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-background px-4">
        <div className="w-full" style={{ maxWidth: "28rem" }}>
          <LoadingState label="Loading your account" rows={3} />
        </div>
      </div>
    );
  }

  if (me.error instanceof ApiError && me.error.status === 401) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  if (me.isError) return <>{children}</>;

  const needsOnboarding = me.data.onboarding.currentStep !== null;
  if (needsOnboarding && !onWelcome) return <Navigate to="/welcome" replace />;
  if (!needsOnboarding && onWelcome) return <Navigate to="/" replace />;
  return <>{children}</>;
}
